import { BitkitAlert, BitkitAlertProps, BitkitProgressBar, rem } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Card, type CardRootProps } from '@chakra-ui/react/card';
import { LinkBox } from '@chakra-ui/react/link';
import { ReactNode, useMemo, useState } from 'react';
import { useInterval } from 'usehooks-ts';

import BuildItemLowerSection from '@/components/BuildListItem/BuildItemLowerSection';
import BuildItemUpperSection from '@/components/BuildListItem/BuildItemUpperSection';
import { BuildItemProps } from '@/components/BuildListItem/types/BuildItemProps';
import ColorBar from '@/components/ColorBar/ColorBar';
import {
  Build,
  calculateDuration,
  calculateProgressPercentage,
  getBuildStatusColors,
  isBuildRunning,
  PublicApiBuild,
} from '@/models/Build';
import AppForAppPage from '@/pages/App/types/AppForAppPage';

type BuildProgressBarProps = {
  build: Build;
  previousBuild?: Build | PublicApiBuild;
};
const BuildProgressBar = ({ build, previousBuild }: BuildProgressBarProps) => {
  const [progress, setProgress] = useState(0);
  const previousBuildDuration = useMemo(() => previousBuild && calculateDuration(previousBuild), [previousBuild]);

  useInterval(
    () => {
      if (!previousBuildDuration) {
        return;
      }

      const actualBuildDuration = calculateDuration(build);
      setProgress(calculateProgressPercentage(actualBuildDuration, previousBuildDuration));
    },
    build ? 1000 : null,
  );

  return (
    <BitkitProgressBar
      animationDuration="moderate"
      animationName="fade-in"
      label="Estimated build progress"
      left="0"
      position="absolute"
      rangeColor="color/purple/moderate"
      right="0"
      size="4"
      top="0"
      trackColor="transparent"
      value={progress}
    />
  );
};

type BuildListErrorItemProps = {
  message: ReactNode;
  retry: VoidFunction;
} & Omit<BitkitAlertProps, 'variant' | 'messageText'>;
const BuildListErrorItem = ({ message, retry, ...rest }: BuildListErrorItemProps) => (
  <BitkitAlert
    variant="critical"
    action={{ label: 'Retry', onClick: () => retry?.() }}
    messageText={message}
    {...rest}
  />
);

type BuildListItemProps = {
  buildItem: BuildItemProps;
  app?: AppForAppPage;
} & CardRootProps;

const BuildListItem = ({ buildItem, app, ...rest }: BuildListItemProps) => {
  const { build, previousBuild, showAppInfo, canRunBuild, onRebuildClick, onAbortClick } = buildItem;

  return (
    <Card.Root
      asChild
      role="group"
      className="group"
      display="flex"
      interactive
      // clip (not hidden) so metadata can't leak past the card horizontally, while overflow-y stays visible
      // — the inline (non-portalled) BitkitTooltip must escape above the card. hidden here would force
      overflowX="clip"
      overflowY="visible"
      {...rest}
    >
      <LinkBox>
        <ColorBar color={getBuildStatusColors(build).color} borderLeftRadius={rem(7)} />
        <Box position="relative" flexGrow={1} flexShrink={1} minWidth="0">
          {isBuildRunning(build) && <BuildProgressBar build={build} previousBuild={previousBuild} />}
          <BuildItemUpperSection
            build={build}
            app={app}
            showAppInfo={showAppInfo}
            canRunBuild={canRunBuild}
            onRebuildClick={onRebuildClick}
            onAbortClick={onAbortClick}
          />
          <BuildItemLowerSection build={build} showBuildNumberOnMobile={!showAppInfo} />
        </Box>
      </LinkBox>
    </Card.Root>
  );
};

export { BuildListErrorItem };
export default BuildListItem;
